import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";

// --- Silence benign ResizeObserver "loop completed" error ---
// Radix Select/Popover occasionally triggers this. The browser already recovers
// automatically, but CRA's dev overlay and webpack-dev-server show it as a
// full-screen fatal error. We (1) wrap the native ResizeObserver to schedule
// callbacks via rAF (which breaks the loop detection) and (2) suppress any
// residual window-level error events that still mention the message.
const RO_MESSAGES = [
  'ResizeObserver loop completed with undelivered notifications.',
  'ResizeObserver loop limit exceeded',
];

if (typeof window !== 'undefined' && window.ResizeObserver) {
  const NativeRO = window.ResizeObserver;
  class PatchedResizeObserver extends NativeRO {
    constructor(callback) {
      super((entries, observer) => {
        window.requestAnimationFrame(() => {
          try { callback(entries, observer); } catch { /* ignore */ }
        });
      });
    }
  }
  window.ResizeObserver = PatchedResizeObserver;
}

const stopRO = (e) => {
  const msg = e?.message || e?.reason?.message || '';
  if (RO_MESSAGES.some(m => typeof msg === 'string' && msg.includes(m))) {
    e.stopImmediatePropagation?.();
    e.preventDefault?.();
    return true;
  }
  return false;
};
window.addEventListener('error', stopRO, true);
window.addEventListener('unhandledrejection', stopRO, true);

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);

// Register PWA Service Worker.
// When a NEW service worker is found we claim it immediately (skipWaiting) and
// hard-reload the page ONCE per session so stale JS bundles from a previous
// version never linger — this fixed the April 20 camera regression.
// We cap reloads at 1 per browser session (sessionStorage flag) so a user on a
// stale cache can never end up in a reload loop (April 22 pilot loop bug).
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').then((reg) => {
      const activateWaiting = () => {
        if (reg.waiting) reg.waiting.postMessage({ type: 'SKIP_WAITING' });
      };
      // If a waiting SW already exists and we already have a controller, it's
      // safe to skip it (the previous version is what's currently controlling).
      if (reg.waiting && navigator.serviceWorker.controller) activateWaiting();
      reg.addEventListener('updatefound', () => {
        const nw = reg.installing;
        if (!nw) return;
        nw.addEventListener('statechange', () => {
          if (nw.state === 'installed' && navigator.serviceWorker.controller) {
            activateWaiting();
          }
        });
      });
      // Check for updates every 5 minutes (was 60 s — too aggressive and caused
      // pilot-module reload loops when cached bundles briefly lagged).
      setInterval(() => reg.update().catch(() => {}), 5 * 60 * 1000);
    }).catch(() => {});

    navigator.serviceWorker.addEventListener('controllerchange', () => {
      // Reload AT MOST once per tab session — prevents infinite loops when a
      // stale cached SW keeps getting detected as new on every fetch cycle.
      if (sessionStorage.getItem('carloo_sw_reloaded') === '1') return;
      sessionStorage.setItem('carloo_sw_reloaded', '1');
      window.location.reload();
    });
  });
}
